/**
 * The corner close: the ✕ in the top-right of every reference modal.
 *
 * @remarks
 * Mike, 2026-09-28: the map modal should close from its top-right corner as
 * well as from the Close at its foot, "like the standard design for modals
 * do". A player who opened a dialog to look something up expects the way out
 * where they first look for it, and on a tall map or a long journal the Close
 * at the foot of the dialog is a scroll away. The ✕ adds that way out and
 * keeps the Close button. Because this is the standard for modals, the
 * journal, voices and account dialogs get the ✕ too; a map that closed from
 * its corner while the journal did not would teach the player two rules.
 *
 * **The same route out as the Close.** The button closes the `<dialog>` it
 * sits in (`closest('dialog').close()`), which fires the element's `close`
 * event. That makes it Escape's route and the Close button's route: the
 * dialog's own `onClose` drops the open flag, and the shell sends focus on
 * to the directive line. It never sets the open flag itself. The page behind
 * a modal is inert, so focus sent there before the element has closed would
 * be refused.
 *
 * **Why it comes last in the source.** Chromium's `showModal()` focuses the
 * first focusable element in the dialog. If the ✕ came first, opening the
 * map from the keyboard would land on the button that closes it, and one
 * Enter too many would shut the dialog it had just opened. Placed last and
 * positioned into the corner with CSS, it leaves the first focus where it
 * was and comes last in the Tab order. That is the same arrangement Radix and
 * most modal libraries use.
 *
 * **Its name.** "Close map", "Close journal" and so on, never a bare "Close":
 * two buttons called "Close" in one dialog would give a screen reader two
 * identical names for one action, and the pair would stop being addressable
 * by name.
 *
 * @packageDocumentation
 */

/** Props for {@link ModalCornerCloseComponent}. */
export interface ModalCornerCloseComponentProps {
	/** The button's accessible name: "Close" and what closes, e.g. `Close map`. */
	readonly label: string;
}

/**
 * Render the ✕ that closes the enclosing `<dialog>` from its top-right corner.
 *
 * @param props - {@link ModalCornerCloseComponentProps}
 * @returns The corner close button
 */
export const ModalCornerCloseComponent = ({ label }: ModalCornerCloseComponentProps) => (
	<button
		aria-label={label}
		className="modal-corner-close"
		onClick={(event) => {
			event.currentTarget.closest('dialog')?.close();
		}}
		title={label}
		type="button"
	>
		<CrossIcon />
	</button>
);

/**
 * The ✕ glyph, drawn with the control row's stroke so it belongs to the same
 * icon set. It is decorative: the button is named by its `aria-label`, so the
 * icon is hidden from assistive technology.
 *
 * @returns The icon
 */
const CrossIcon = () => (
	<svg
		aria-hidden="true"
		className="modal-corner-close__icon"
		fill="none"
		focusable="false"
		height="16"
		stroke="currentColor"
		strokeLinecap="round"
		strokeWidth={1.6}
		viewBox="0 0 24 24"
		width="16"
	>
		<path d="M6 6l12 12" />
		<path d="M18 6 6 18" />
	</svg>
);
